import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import {
  apiLoadErrorText,
  prcVersionArg,
  useGetPrcBreakEvenQuery,
  useGetPrcModelResultQuery,
  useGetPrcVersionQuery,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconArrowLeft } from '@shared/ui/icons';
import { lightTheme } from '@shared/theme/tokens';
import { fleetLeading, fleetType, formatDateTime, typo } from '@entities/fleet';
import { formatIsoDate, prcStatusLabel, wasApproved } from '@entities/prc';
import { Notice } from '@widgets/FleetRegistry/ui';
import { formatHours, formatNumber, formatServerMoney, personName } from '../format';
import { BreakEvenChart } from '../tabs/BreakEvenChart';
import {
  BreakEvenInputs,
  BreakEvenKpis,
  BreakEvenNeed,
  BreakEvenVerdict,
  SensitivityTable,
} from '../tabs/breakEvenParts';
import { ArticlesByYear } from '../tabs/modelParts';
import { NARROW } from '../ui';

/**
 * Печать: A4 альбомная, на бумагу — только лист формы. Навигация, кнопки и
 * подсказки скрыты, строки таблиц не режутся, шапки таблиц повторяются.
 * Лист всегда в светлой теме: бумага белая и при тёмной теме экрана.
 *
 * Узкая раскладка листа — по ширине самого листа (container query), а не по
 * ширине окна: при печати браузер может сверять media-запросы с бумагой по
 * умолчанию (книжной), хотя лист альбомный, — и сложил бы шапку в столбик.
 */
const PrintStyle = createGlobalStyle`
  @page {
    size: A4 landscape;
    margin: 10mm 12mm;
  }

  @media print {
    html,
    body,
    #root {
      height: auto;
      background: ${({ theme }) => theme.colors.backgroundElevated};
    }

    body {
      color: ${({ theme }) => theme.colors.text.primary};
    }

    tr,
    th,
    td {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    thead {
      display: table-header-group;
    }

    /* Прокрутка таблиц на бумаге не нужна — показать целиком. */
    [role='region'] {
      overflow: visible !important;
    }
  }
`;

/** Стол под листом — светлый и при тёмной теме приложения: панель над листом читается. */
const Page = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(4)} ${theme.spacing(10)}`};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  color-scheme: light;

  @media print {
    min-height: 0;
    padding: 0;
    background: none;
  }
`;

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  max-width: 1120px;
  margin: 0 auto ${({ theme }) => theme.spacing(4)};

  & > .hint {
    flex: 1 1 260px;
    margin: 0;
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  @media print {
    display: none;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: ${fleetType.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Лист формы: на экране — бумага на столе, на печати — без рамки. */
const Sheet = styled.article`
  max-width: 1120px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.spacing(8)} ${theme.spacing(10)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  box-shadow: ${({ theme }) => theme.shadow.md};
  container: prc / inline-size;

  @media screen and (max-width: ${NARROW}) {
    padding: ${({ theme }) => theme.spacing(4)};
  }

  @media print {
    max-width: none;
    padding: 0;
    border: none;
    box-shadow: none;

    /* Таблицы на бумаге плотнее: форма ближе к листу книги и короче на страницу. */
    & table {
      font-size: 9pt;
    }

    & table th,
    & table td {
      padding-top: 3px;
      padding-bottom: 3px;
    }
  }
`;

const Head = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(6)};
  align-items: start;
  padding-bottom: ${({ theme }) => theme.spacing(5)};
  border-bottom: 2px solid ${({ theme }) => theme.colors.text.primary};

  @container prc (max-width: ${NARROW}) {
    grid-template-columns: minmax(0, 1fr);
  }

  & h1 {
    margin: 0;
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    text-wrap: balance;
  }

  & .model {
    margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
  }

  & .meta {
    margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
    font-size: ${fleetType.body};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Гриф утверждения — как «УТВЕРЖДАЮ» на листе книги ЭММ. */
const Stamp = styled.div<{ $approved: boolean }>`
  min-width: 240px;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: ${({ $approved, theme }) =>
    $approved
      ? `1px solid ${theme.colors.text.primary}`
      : `1px dashed ${theme.colors.borderStrong}`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  break-inside: avoid;

  & > .title {
    margin: 0 0 ${({ theme }) => theme.spacing(1)};
    font-size: ${fleetType.base};
    font-weight: 700;
    letter-spacing: 0.12em;
  }

  & > p {
    margin: 0;
  }

  & > .muted {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Block = styled.section`
  margin-top: ${({ theme }) => theme.spacing(6)};
  break-inside: auto;

  & > h2 {
    margin: 0 0 ${({ theme }) => theme.spacing(3)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    break-after: avoid;
  }

  & > .lead {
    margin: 0 0 ${({ theme }) => theme.spacing(3)};
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(6)}`};
  margin: ${({ theme }) => `${theme.spacing(5)} 0 0`};
  break-inside: avoid;

  @container prc (max-width: ${NARROW}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  & dt {
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: 2px 0 0;
    font-size: ${fleetType.lead};
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Foot = styled.footer`
  margin-top: ${({ theme }) => theme.spacing(8)};
  padding-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** «21» октября 2026 г. — как дата под грифом. */
function stampDate(iso: string | null): string {
  if (!iso) return '«___» __________ 20__ г.';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `«${String(d.getDate()).padStart(2, '0')}» ${MONTHS[d.getMonth()]} ${d.getFullYear()} г.`;
}

/**
 * Печатный вид «Калькуляции (форма)» по машине: гриф утверждения, строки
 * расчёта по годам (те же данные, что панель модели) и блоки безубыточности.
 * Печать и PDF — средствами браузера (`window.print()`).
 */
export function PrcPrintPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const modelId = params.get('model') ?? '';
  const variant: 1 | 2 = params.get('variant') === '1' ? 1 : 2;
  const yearParam = Number(params.get('year'));
  const sensitivity = params.get('sensitivity') ?? undefined;
  const autoprint = params.get('print') === '1';
  // Вариант условий — его цены на листе; нет — основной расчёт.
  const conditions = params.get('cond') ?? undefined;

  const head = useGetPrcVersionQuery(prcVersionArg(id, undefined, conditions), { skip: !id });
  const result = useGetPrcModelResultQuery(
    { id, modelId, variant: conditions },
    { skip: !id || !modelId },
  );
  const version = head.data?.version;
  const year =
    version && version.params.years.includes(yearParam) ? yearParam : version?.params.base_year;
  const be = useGetPrcBreakEvenQuery(
    { id, modelId, variant, year, sensitivity, conditions },
    { skip: !id || !modelId || year === undefined || result.data?.model.result === null },
  );

  const model = result.data?.model;
  const ready = Boolean(version && model && (be.data || be.isError || model.result === null));
  const printed = useRef(false);

  useEffect(() => {
    if (version && model) document.title = `Калькуляция (форма) — ${model.model_name}`;
  }, [version, model]);

  // Открыли кнопкой «Печать / PDF» — диалог печати сразу, когда лист готов.
  useEffect(() => {
    if (!autoprint || !ready || printed.current) return;
    printed.current = true;
    void document.fonts.ready.then(() => requestAnimationFrame(() => window.print()));
  }, [autoprint, ready]);

  const error = head.error ?? result.error;
  const approved = version ? wasApproved(version.status) : false;
  const y = model?.result?.years[String(year ?? '')];

  return (
    <ThemeProvider theme={lightTheme}>
      <PrintStyle />
      <Page>
        <Bar>
          <BackLink to={`/agents/econ/prc/${id}`}>
            <IconArrowLeft size={16} />К версии
          </BackLink>
          <p className="hint">
            {typo(
              'Печатный вид формы. В окне печати выберите принтер или «Сохранить как PDF», ориентация — альбомная.',
            )}
          </p>
          <Button type="button" $variant="primary" disabled={!ready} onClick={() => window.print()}>
            Печать / PDF
          </Button>
        </Bar>

        <Sheet aria-busy={!ready}>
          {error !== undefined ? (
            <Notice $tone="danger" role="alert">
              <span className="text">
                {apiLoadErrorText(error, 'Не удалось собрать форму.', 'Нет доступа к ПРЦ.')}
              </span>
            </Notice>
          ) : !version || !model ? (
            <>
              <SkeletonLine $height={28} $width="50%" />
              <SkeletonLine $width="30%" />
              <SkeletonLine $height={200} />
            </>
          ) : (
            <>
              <Head>
                <div>
                  <h1>Калькуляция планово-расчётной цены машино-часа</h1>
                  <p className="model">{model.model_name}</p>
                  <p className="meta">
                    {[
                      model.category_name,
                      head.data?.variant
                        ? `версия «${version.label}», вариант «${head.data.variant.name}»`
                        : `версия «${version.label}»`,
                      `базовый год ${version.params.base_year}`,
                      `горизонт ${version.params.years[0]}–${version.params.years[version.params.years.length - 1]}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <Stamp $approved={approved}>
                  {approved ? (
                    <>
                      <p className="title">УТВЕРЖДАЮ</p>
                      <p>{personName(version.approved_by_name)}</p>
                      <p>{stampDate(version.approved_at)}</p>
                      <p className="muted">Действует с {formatIsoDate(version.valid_from)}</p>
                      {version.status === 'superseded' && <p className="muted">Версия заменена</p>}
                    </>
                  ) : (
                    <>
                      <p className="title">НЕ УТВЕРЖДЕНО</p>
                      <p className="muted">
                        Статус: {prcStatusLabel(version.status).toLowerCase()}
                      </p>
                      <p className="muted">Цена не действует в справочнике техники</p>
                    </>
                  )}
                </Stamp>
              </Head>

              {model.result === null ? (
                <Block>
                  <Notice $tone="danger">
                    <span className="text">
                      {typo(`ПРЦ не посчитана: ${model.error ?? 'ошибка расчёта'}`)}
                    </span>
                  </Notice>
                </Block>
              ) : (
                <>
                  {y && (
                    <Facts>
                      <div>
                        <dt>Цена без НДС, {year}</dt>
                        <dd>{formatServerMoney(y.price)}</dd>
                      </div>
                      <div>
                        <dt>Цена с НДС</dt>
                        <dd>{formatServerMoney(y.price_with_vat)}</dd>
                      </div>
                      <div>
                        <dt>Себестоимость</dt>
                        <dd>{formatServerMoney(y.cost)}</dd>
                      </div>
                      <div>
                        <dt>Годовой режим</dt>
                        <dd>{formatNumber(model.result.annual_hours, 1)} маш.-ч</dd>
                      </div>
                    </Facts>
                  )}

                  <Block>
                    <h2>Расчёт по годам</h2>
                    <ArticlesByYear
                      result={model.result}
                      years={version.params.years}
                      label="Статьи затрат и цена"
                    />
                  </Block>

                  {be.data && (
                    <>
                      <Block>
                        <h2>Безубыточность</h2>
                        <p className="lead">
                          {typo(
                            `${be.data.year} год, вариант ${be.data.variant} (${be.data.variant === 1 ? 'коммерческий' : 'управленческий'}), плановая загрузка ${formatHours(be.data.inputs.q_plan)} в месяц.`,
                          )}
                        </p>
                        <BreakEvenVerdict data={be.data} />
                      </Block>
                      <Block>
                        <BreakEvenKpis data={be.data} />
                        <BreakEvenNeed data={be.data} />
                      </Block>
                      <Block>
                        <h2>Исходные данные</h2>
                        <BreakEvenInputs data={be.data} />
                      </Block>
                      <Block>
                        <h2>График безубыточности</h2>
                        <BreakEvenChart data={be.data} />
                      </Block>
                      <Block>
                        <h2>Чувствительность</h2>
                        <SensitivityTable data={be.data} />
                      </Block>
                    </>
                  )}
                  {be.isError && (
                    <Block>
                      <Notice $tone="warning">
                        <span className="text">
                          {apiLoadErrorText(
                            be.error,
                            'Безубыточность не посчитана.',
                            'Нет доступа к ПРЦ.',
                          )}
                        </span>
                      </Notice>
                    </Block>
                  )}
                </>
              )}

              <Foot>
                {typo(
                  `Сформировано в ОСНОВЕ ${formatDateTime(new Date().toISOString())}. Расчёт ОСНОВЫ по методике книги «Калькуляция ЭММ»; суммы — ₽ на маш.-час, если не сказано иное.`,
                )}
              </Foot>
            </>
          )}
        </Sheet>
      </Page>
    </ThemeProvider>
  );
}
